import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Loader2, Lock, UserCheck, ArrowLeft, EyeOff, Eye, RefreshCw } from "lucide-react";
import { isAxiosError } from "axios";
import {
  acceptInvitationSchema,
  AcceptInvitationFormData,
} from "@/lib/validators/auth";
import {
  useValidateInvitationToken,
  useAcceptInvitation,
} from "../security/hooks/useTeam";
import { useState } from "react";
import { LOGOPATH } from "@/lib/paths";

export default function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    data: invitationDetails,
    isLoading: isTokenValidating,
    isError: isTokenInvalid,
    error: validationError,
    refetch: revalidateToken,
  } = useValidateInvitationToken(token);
  const { mutate: acceptInvitation, isPending: isAccepting } =
    useAcceptInvitation();

  const validationStatus = isAxiosError(validationError)
    ? validationError.response?.status
    : undefined;
  // 404/410/400 = the link itself is bad. Anything else (401/403, 5xx,
  // network) is a verification failure — don't mislabel it as an invalid link.
  const isLinkRejected =
    isTokenInvalid &&
    validationStatus !== undefined &&
    [400, 404, 410].includes(validationStatus);
  const isVerificationFailure = isTokenInvalid && !isLinkRejected;

  const form = useForm<AcceptInvitationFormData>({
    resolver: zodResolver(acceptInvitationSchema),
    defaultValues: {
      password: "",
      passwordConfirmation: "",
    },
  });

  const onSubmit = (data: AcceptInvitationFormData) => {
    if (token) {
      acceptInvitation({ token, payload: data, user: invitationDetails.name });
    }
    // searchParams.delete("token");
  };

  if (isTokenValidating) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-slate-600">Validating invitation...</p>
        </div>
      </div>
    );
  }

  if (isVerificationFailure) {
    return (
      <div className="min-h-screen bg-white flex">
        <div className="absolute top-8 left-8 z-20">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 flex items-center justify-center overflow-hidden">
              <img
                src={LOGOPATH}
                alt="Logo"
                className="w-10 h-10 object-contain"
              />
            </div>
            <h1 className="text-2xl font-bold text-slate-800">RoboBank</h1>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <RefreshCw className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-800 mb-4">
              We Couldn't Verify This Invitation
            </h2>
            <p className="text-slate-600 mb-8">
              {validationStatus === 401 || validationStatus === 403
                ? "We were unable to confirm this invitation link. This is usually temporary — please try again, or contact your administrator if the problem persists."
                : "Something went wrong while checking this invitation link. Please try again, or contact your administrator if the problem persists."}
            </p>

            <div className="space-y-3">
              <Button
                onClick={() => revalidateToken()}
                className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg"
              >
                <RefreshCw className="w-5 h-5 mr-2" />
                Try Again
              </Button>
              <Button
                asChild
                variant="outline"
                className="w-full h-12 font-medium rounded-lg"
              >
                <Link to="/login">Go to Login</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLinkRejected || !invitationDetails?.valid) {
    return (
      <div className="min-h-screen bg-white flex">
        <div className="absolute top-8 left-8 z-20">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 flex items-center justify-center overflow-hidden">
              <img
                src={LOGOPATH}
                alt="Logo"
                className="w-10 h-10 object-contain"
              />
            </div>
            <h1 className="text-2xl font-bold text-slate-800">RoboBank</h1>
          </div>
        </div>

        <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-indigo-50 via-indigo-100/50 to-white">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/5 via-transparent to-indigo-400/5"></div>
          <div className="flex items-center justify-center w-full p-12">
            <div className="w-full max-w-lg h-96 bg-indigo-100/30 rounded-2xl flex items-center justify-center border border-indigo-200/50">
              <div className="text-center">
                <div className="w-24 h-24 bg-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-12 h-12 text-white" />
                </div>
                <p className="text-indigo-600 font-medium">
                  Invalid Invitation
                </p>
                <p className="text-sm text-indigo-400 mt-2">
                  This link has expired or been used
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 lg:w-1/2 flex items-center justify-center p-8">
          <div className="w-full max-w-md text-center">
            <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-800 mb-4">
              Invalid Invitation
            </h2>
            <p className="text-slate-600 mb-8">
              This invitation link is invalid, has been used, or has expired.
              Please contact your administrator for a new invitation.
            </p>

            <Button
              asChild
              className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg"
            >
              <Link to="/login">Go to Login</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex">
      <div className="absolute top-8 left-8 z-20">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 flex items-center justify-center overflow-hidden">
            <img
              src={LOGOPATH}
              alt="Logo"
              className="w-10 h-10 object-contain"
            />
          </div>
          <h1 className="text-2xl font-bold text-slate-800">RoboBank</h1>
        </div>
      </div>

      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-indigo-50 via-indigo-100/50 to-white">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/5 via-transparent to-indigo-400/5"></div>
        <div className="flex items-center justify-center w-full p-12">
          <div className="w-full max-w-lg h-96 bg-indigo-100/30 rounded-2xl flex items-center justify-center border border-indigo-200/50">
            <div className="text-center">
              <div className="w-24 h-24 bg-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <UserCheck className="w-12 h-12 text-white" />
              </div>
              <p className="text-indigo-600 font-medium">Team Invitation</p>
              <p className="text-sm text-indigo-400 mt-2">
                Join the RoboBank Admin team
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <UserCheck className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-slate-800 mb-2">
              Welcome, {invitationDetails.name}!
            </h2>
            <p className="text-slate-600">
              You've been invited to join the RoboBank Admin team as a{" "}
              <span className="font-medium text-slate-800">
                {invitationDetails.role
                  .split("-")
                  .map(
                    (word: string) =>
                      word.charAt(0).toUpperCase() + word.slice(1)
                  )
                  .join(" ")}
              </span>
              . Please create a password to activate your account.
            </p>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-slate-700">
                      New Password
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter new password"
                          className="h-12 pr-12 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500 bg-white"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                        >
                          {showPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="passwordConfirmation"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-slate-700">
                      Confirm New Password
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Confirm new password"
                          className="h-12 pr-12 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500 bg-white"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                disabled={isAccepting}
                className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {isAccepting ? (
                  "Creating Account..."
                ) : (
                  <>
                    <UserCheck className="w-5 h-5 mr-2" />
                    Create Account & Login
                  </>
                )}
              </Button>
            </form>
          </Form>

          <div className="mt-8 text-center space-y-4">
            <p className="text-sm text-slate-600">
              Already have an account?{" "}
              <Link
                to="/login"
                className="text-indigo-600 hover:text-indigo-500 font-medium"
              >
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
